/**
 * The visible contract board (plan §6 M2-08): a station's generated offers for the current lease cycle minus
 * every id the career has accepted, completed or failed (abandoned jobs count as failed and never come back).
 *
 * M7 S14 (spec §9): a board closed to the player under Tethys retaliation (`boardsClosed`: Tethys and Varro boards;
 * `dockingRefused`: every Tethys service) shows nothing, so no offer there can be found or accepted.
 *
 * Career layer: pure, not per-frame.
 */
import { stationDef } from '../data/m2'
import type { M2Data, StationId } from '../data/m2Schemas'
import { m2RefusalStation, retaliationRefuses } from '../story/refusals'
import type { Career } from '../career/types'
import { boardCycle, generateBoard, type ContractOffer } from './generate'

/** True when `id` is accepted, completed or failed. */
export function isContractTaken(career: Career, id: string): boolean {
  const c = career.contracts
  return c.active.some((a) => a.id === id) || c.completedIds.includes(id) || c.failedIds.includes(id)
}

/** True when retaliation closes `stationId`'s board to the player (spec §9). */
export function boardClosed(career: Career, data: Pick<M2Data, 'stations'>, stationId: StationId): boolean {
  return retaliationRefuses(career, m2RefusalStation(stationDef(data, stationId)), 'contracts') !== null
}

/** The offers `stationId` shows for `cycleIndex` (default: the career's current lease cycle). */
export function visibleBoard(data: M2Data, career: Career, stationId: StationId, cycleIndex: number = boardCycle(career)): ContractOffer[] {
  if (boardClosed(career, data, stationId)) return []
  return generateBoard(data, career, stationId, cycleIndex).filter((o) => !isContractTaken(career, o.id))
}

/** The offer `id` on `stationId`'s current board, or null when it is not (or no longer) offered. */
export function findOffer(data: M2Data, career: Career, stationId: StationId, id: string): ContractOffer | null {
  return visibleBoard(data, career, stationId).find((o) => o.id === id) ?? null
}
